import './ContentBuilderTemplate.scss';
import './ContentBuilderTemplate78.scss';

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../../utils/bem';
import { usePropsHandler } from '../../EventInterceptor/usePropsHandler';
import { Typography } from '@dataartdev/uikit/Typography';
import { cnContentBuilderTemplate } from './index';
import { Grid, GridItem } from '@dataartdev/uikit/Grid';
import { Button } from '@dataartdev/uikit/Button';
import { IconArrowRight } from '@dataartdev/uikit/IconArrowRight';
import { IRoot78 } from './models/CTI_78.props';
import { prepareButtonAttrs } from '../../../utils/prepareButtonAttrs';
import { CtaScrollerBtn } from '../../CtaScrollerBtn';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { useDispatch, useSelector } from 'react-redux';
import { screenSelector, setContactButtonVisible } from '../../../../store';
import { Picture } from '@dataartdev/uikit/Picture';
import { Slider, SliderItem } from '@dataartdev/uikit/Slider';
import { FeaturedCard } from '../../FeaturedCard/FeaturedCard';

const cnHeroVidSection = cn('HeroVidSection');

export const ContentBuilderTemplate: React.FC<IRoot78> = props => {
  const {
    type,
    text,
    title,
    button,
    video = 'https://websitesapi.dataart.com/media/vvdcbbpk/hero.mp4',
    preview,
    contentId,
    isInverted,
    insights = [],
  } = usePropsHandler(cnContentBuilderTemplate(), props);

  const typeNumber = type.toString();
  const buttonAttr = prepareButtonAttrs(
    button?.link,
    button?.title,
    button?.targetBlank
  );
  const buttonView = button?.color === 'blue' ? 'primary' : 'secondary';
  const contact2Ref = useRef(null);
  const [isVisible, isInitiallyChecked] = useIntersectionObserver(
    contact2Ref,
    {
      rootMargin: '0px',
      threshold: 1.0,
    },
    0
  );

  const dispatch = useDispatch();
  const contactButtonVisible = {
    isVisible,
    isInitiallyChecked,
  };

  useEffect(() => {
    dispatch(setContactButtonVisible(contactButtonVisible));
  }, [isVisible, isInitiallyChecked, setContactButtonVisible]);

  const [useVideo, setUseVideo] = useState(true);

  useEffect(() => {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntriesByName('first-contentful-paint')) {
        if (entry.startTime > 3000) {
          setUseVideo(false);
        }
      }
    });
    observer.observe({ type: 'paint', buffered: true });

    return () => observer.disconnect();
  }, []);

  const { isMobile } = useSelector(screenSelector);
  const loop = isMobile ? true : insights.length >= 5;

  const sliderOptions = {
    loop: loop,
    watchOverflow: true,
    navigation: insights?.length > 1,
    slidesPerView: 1.1,
    spaceBetween: 24,
    breakpoints: {
      767: {
        slidesPerView: 1.2,
      },
      1200: {
        slidesPerView: 2.2,
      },
    },
  };

  return (
    <div
      className={cnContentBuilderTemplate({ type: typeNumber })}
      id={contentId}
    >
      <div className={cnHeroVidSection({ isInverted: isInverted })}>
        {video && useVideo ? (
          <div className={cnHeroVidSection('Video')}>
            <video autoPlay muted loop>
              <source src={video} type="video/mp4" />
            </video>
          </div>
        ) : (
          <Picture className={cnHeroVidSection('Preview')} {...preview} />
        )}
        <div className="container">
          <div className={cnHeroVidSection('Content')}>
            <Grid
              breakpoints={{
                desktop: { cols: 12 },
                tablet: { cols: 12 },
                mobile: { cols: 12 },
              }}
              className={cnHeroVidSection('Inner')}
            >
              <GridItem
                breakpoints={{
                  desktop: { col: 5 },
                  tablet: { col: 6 },
                  mobile: { col: 12 },
                }}
                className={cnHeroVidSection('Info')}
              >
                {title && (
                  <Typography.Title
                    as="h1"
                    size="hg"
                    className={cnHeroVidSection('Title')}
                    parse
                  >
                    {title}
                  </Typography.Title>
                )}
                {text && (
                  <Typography.Text
                    size="l"
                    as="p"
                    className={cnHeroVidSection('Text')}
                    parse
                  >
                    {text}
                  </Typography.Text>
                )}
                {Object.keys(buttonAttr).length > 0 &&
                  ('isCTA' in buttonAttr ? (
                    <CtaScrollerBtn
                      view={buttonView}
                      className={
                        button?.color === 'red'
                          ? cnHeroVidSection('Btn_Red')
                          : cnHeroVidSection('Btn')
                      }
                      ref={contact2Ref}
                      {...buttonAttr}
                    />
                  ) : (
                    <Button
                      iconRight={IconArrowRight}
                      view={buttonView}
                      className={
                        button?.color === 'red'
                          ? cnHeroVidSection('Btn_Red')
                          : cnHeroVidSection('Btn')
                      }
                      ref={contact2Ref}
                      {...buttonAttr}
                    />
                  ))}
              </GridItem>
              {insights?.length > 0 && (
                <GridItem
                  breakpoints={{
                    desktop: { col: 6, colStart: 7 },
                    tablet: { col: 6 },
                    mobile: { col: 12 },
                  }}
                  className={cnHeroVidSection('Carousel-Wrapper')}
                >
                  <Slider
                    {...sliderOptions}
                    className={cnHeroVidSection('Carousel')}
                  >
                    {insights?.map(item => (
                      <SliderItem>
                        <FeaturedCard {...item} />
                      </SliderItem>
                    ))}
                  </Slider>
                </GridItem>
              )}
            </Grid>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentBuilderTemplate;
